<template>
  <b-card class="card-box card__history-submission">
    <h5 class="font-weight-bold mb-4">Submission History</h5>

    <div v-if="groupedLatestEvents && groupedLatestEvents.length">
      <div v-for="(item, index) in groupedLatestEvents" :key="index">
        <div v-if="item && item.message" class="my-2">
          <div class="text-black-50 text__event-time">{{ formattedTime(item.message) }}</div>
          <div class="text__event-message">
            {{ content(item.message) }}
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { datetimeMixin } from '@/mixins/datetimeMixin';
import _ from 'lodash';
import Post from '@/models/Post';
import EvidenceSubmission from '@/models/EvidenceSubmission';

export default {
  mixins: [datetimeMixin],

  props: {
    items: {
      type: Array,
      default: () => []
    },
    partPostIds: {
      type: Object,
      default: () => ({})
    },
    evidenceSubmissionId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {};
  },

  computed: {
    isIssuer() {
      return this.$currentUser.isIssuer();
    },

    evidenceSubmission() {
      const id = Number.parseInt(this.evidenceSubmissionId, 10);

      return id && id > 0 ? EvidenceSubmission.find(id) : null;
    },

    assessor() {
      return this.evidenceSubmission?.assessor_id;
    },

    isOwner() {
      return this.$currentUser.isOwnerOf(this.evidenceSubmission?.learning_unit_id);
    },

    /**
     * Computes the latest events grouped by eventType.
     * It filters out items without eventType or createdAt,
     * groups them by eventType, and returns the latest event
     */
    groupedLatestEvents() {
      // Filter valid items that have eventType and createdAt
      const validItems = this.items.filter(
        (item) => item.message?.eventType && item.message?.createdAt
      );

      // Group by eventType
      const groupedEvents = _.groupBy(validItems, 'message.eventType');

      // groupedEvents will contain arrays of events grouped by eventType
      const latestEvents = _(groupedEvents)
        .omit('AssessmentEvaluationChangedByIssuer') // Exclude AssessmentEvaluationChangedByIssuer
        .values() // Get the values (arrays of events)
        .map((group) => _.maxBy(group, 'message.createdAt')) // Get the latest event in each group
        .value(); // Get the latest event for each eventType

      // If the user is an issuer, get the latest events for AssessmentEvaluationChangedByIssuer
      const partLatestEvents = this.getLatestEventsByPart(
        groupedEvents.AssessmentEvaluationChangedByIssuer
      );

      // Combine both arrays and order by createdAt in descending order
      return _.orderBy([...latestEvents, ...partLatestEvents], 'message.createdAt', 'desc');
    }
  },

  methods: {
    formattedTime(item) {
      const time = this.contentTime(item);
      return time ? `${time} ${this.cityTimezone} time` : '';
    },

    getIssuerMessage($metadata) {
      const issuer = $metadata?.issuer;
      if (!issuer) return '';

      const { firstname, lastname } = issuer;
      if (!firstname || !lastname) return '';

      const issuerName = `${firstname} ${lastname} (Issuer)`;
      const { postId } = $metadata || {};

      // Handle part rubric update case
      if (postId) {
        const part = this.findPartForPostId(postId);
        if (part) {
          return `Part ${part} rubric recommendation updated by ${issuerName}`;
        }

        return '';
      }

      // Handle micro-credential status case
      if ($metadata?.pass !== undefined) {
        const status = $metadata.pass ? 'awarded' : 'denied';
        return `${issuerName} ${status} micro-credential`;
      }
    },

    getAssessorMessage($metadata, data = null) {
      if (!this.assessor) return '';

      const assessor = $metadata?.assessor;
      if (!assessor) return '';

      const { firstname: assessorFirst, lastname: assessorLast } = assessor;
      if (!assessorFirst || !assessorLast) return '';

      const assessorName = `${assessorFirst} ${assessorLast} (Assessor)`;

      // Handle assessment result case
      if (data?.pass !== undefined) {
        const result = data.pass ? 'an award' : 'a denial';
        return `${assessorName} recommended ${result}`;
      }

      // Handle assignment case
      const owner = $metadata?.owner;
      if (!owner) return '';

      const { firstname: ownerFirst, lastname: ownerLast } = owner;
      if (!ownerFirst || !ownerLast) return '';

      return `${assessorName} was assigned by ${ownerFirst} ${ownerLast}`;
    },

    findPartForPostId(postId) {
      for (const part in this.partPostIds) {
        if (this.partPostIds[part].includes(postId)) {
          return Post.PART_ORDER[part];
        }
      }
    },

    contentTime(item) {
      const format = 'MMM D, YYYY hh:mm a';
      const meta = item.$metadata;

      let time = null;

      switch (item?.eventType) {
        case 'SubmissionSubmitted':
          time = time = item.createdAt;
          break;
        case 'SubmissionAssessedByAssessor':
          if (meta?.assessor) time = item.createdAt;
          break;
        case 'SubmissionGrantedByIssuer':
          if (meta?.issuer) time = item.createdAt;
          break;
        case 'SubmissionAssignedToAssessor':
          if (this.assessor && meta?.assessor) time = item.createdAt;
          break;
        case 'AssessmentEvaluationChangedByIssuer':
          if (meta?.issuer) time = item.createdAt;
          break;

        default:
          return;
      }

      return time ? this.formatedDatetime(time, format) : time;
    },

    content(item) {
      const { eventType, $metadata, data } = item;

      switch (eventType) {
        case 'SubmissionSubmitted':
          return 'Evidence was submitted';

        case 'SubmissionAssessedByAssessor':
          return this.getAssessorMessage($metadata, data);

        case 'SubmissionAssignedToAssessor':
          return this.getAssessorMessage($metadata);

        case 'SubmissionGrantedByIssuer':
          return this.getIssuerMessage($metadata);

        case 'AssessmentEvaluationChangedByIssuer':
          return this.getIssuerMessage($metadata);

        default:
          return '';
      }
    },

    /**
     * Gets the latest assessment event for each part in the learning unit.
     * This method processes assessment events by:
     * 1. Checking if the current user has issuer permissions
     * 2. Grouping events by their associated part using the post ID
     * 3. Finding the most recent event for each part
     *
     * @param {Array} eventsGroups - Array of assessment events to process
     * @returns {Array} Returns an array of the latest events, one per part.
     *                  Returns empty array if user is not an issuer or if no events exist.
     */
    getLatestEventsByPart(eventsGroups) {
      // Return early if user is not an issuer or no events exist
      if ((!this.isIssuer && !this.isOwner) || !eventsGroups?.length) return [];

      return _(eventsGroups)
        .groupBy((item) => this.findPartForPostId(item.message.$metadata?.postId)) // Group events by part
        .values() // Convert grouped object to array of arrays
        .map((group) => _.maxBy(group, 'message.createdAt')) // Get most recent event from each group
        .compact() // Remove any undefined/null values
        .value(); // Execute the chain and return result
    }
  }
};
</script>

<style scoped lang="scss">
.card__history-submission {
  max-width: 910px;
}
</style>
